import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient, formatDate, TICKET_STATUS_LABELS, TICKET_CATEGORY_LABELS, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import AttachmentPicker from "@/components/AttachmentPicker";
import { ArrowLeft, ChatCircle, PaperPlaneTilt, FilePdf, Image as ImageIcon, User, Buildings, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

const publicClient = axios.create({ baseURL: API });

const TICKET_CHIP = {
  new: "bg-[#EEF3FB] text-[#3E5AA8] border-[#D6E0F2]",
  unassigned: "bg-[#FDF2F2] text-[#C83B3B] border-[#F6D5D5]",
  in_progress: "bg-[#FFF9F0] text-[#B96B15] border-[#FCE3C5]",
  waiting_client: "bg-[#F5F1FB] text-[#7A5AA8] border-[#E4D9F3]",
  resolved: "bg-[#ECF9F1] text-[#218A49] border-[#C7EED8]",
  closed: "bg-[#F2F1EC] text-[#68706A] border-[#E4E2DA]",
};

export default function PortalTicket() {
  const { token } = useParams();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [reply, setReply] = useState("");
  const [files, setFiles] = useState([]);
  const [posting, setPosting] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await publicClient.get(`/portal/tickets/by-token/${token}`);
      setTicket(data.ticket);
      setMessages(data.messages);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Lien invalide ou ticket introuvable");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const postMessage = async (e) => {
    e.preventDefault();
    if (!reply.trim()) { toast.error("Message vide"); return; }
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append("body", reply.trim());
      files.forEach((f) => fd.append("files", f));
      await publicClient.post(`/portal/tickets/by-token/${token}/messages`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReply(""); setFiles([]);
      toast.success("Message envoyé");
      fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec");
    } finally { setPosting(false); }
  };

  if (loading) return <section className="max-w-2xl mx-auto px-4 sm:px-6 pt-10 text-[15px] text-[#68706A]">Chargement...</section>;
  if (error || !ticket) {
    return (
      <section className="max-w-2xl mx-auto px-4 sm:px-6 pt-10" data-testid="portal-ticket-error">
        <div className="rounded-2xl border border-[#F6D5D5] bg-[#FDF2F2] px-4 py-5 text-[15px] text-[#C83B3B]">{error}</div>
        <Link to="/portail" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#68706A] hover:text-[#24593C] mt-4">
          <ArrowLeft size={16} weight="bold" /> Retour à l'accueil
        </Link>
      </section>
    );
  }

  const chipCls = TICKET_CHIP[ticket.status] || TICKET_CHIP.new;
  const canReply = ticket.status !== "closed";

  return (
    <section className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 portal-fade-up" data-testid="portal-ticket-page">
      <Link to="/portail" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#68706A] hover:text-[#24593C] transition-colors mb-5">
        <ArrowLeft size={16} weight="bold" /> Retour à l'accueil
      </Link>

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/[0.05] p-5 sm:p-6 mb-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="font-mono text-sm font-semibold text-[#68706A]" data-testid="ticket-reference">{ticket.reference}</div>
            <h1 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-[#1D211F] mt-1 break-words">{ticket.subject}</h1>
            <div className="text-sm text-[#68706A] mt-1">Catégorie : {TICKET_CATEGORY_LABELS[ticket.category] || ticket.category}</div>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${chipCls}`} data-testid="ticket-status">
            {TICKET_STATUS_LABELS[ticket.status]}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-[#68706A] border-t border-neutral-100 pt-4">
          <div>Ouvert le <span className="font-mono text-[#1D211F]">{formatDate(ticket.created_at)}</span></div>
          {ticket.transaction_number && <div>N° transaction : <span className="font-mono text-[#1D211F]">{ticket.transaction_number}</span></div>}
        </div>
      </div>

      {/* Conversation thread */}
      <div className="space-y-4">
        {messages.map((m) => {
          const isMine = m.author_role_display === "Vous";
          return (
            <div key={m.id} className={`flex gap-2.5 ${isMine ? "flex-row-reverse" : "flex-row"}`} data-testid={`portal-msg-${m.id}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isMine ? "bg-[#E7F1EF] text-[#24593C]" : "bg-[#ECF9F1] text-[#218A49]"}`}>
                {isMine ? <User size={18} weight="duotone" /> : <Buildings size={18} weight="duotone" />}
              </div>
              <div className={`max-w-[82%] ${isMine ? "items-end" : "items-start"} flex flex-col`}>
                <div className={`flex items-center gap-2 mb-1 ${isMine ? "flex-row-reverse" : ""}`}>
                  <span className="text-[13px] font-medium text-[#1D211F]">{isMine ? "Vous" : "Service Ecocasse"}</span>
                  <span className="text-[11px] text-neutral-400 font-mono">{formatDate(m.created_at)}</span>
                </div>
                <div className={`px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap break-words ${isMine ? "bg-[#24593C] text-white rounded-2xl rounded-tr-md" : "bg-white border border-black/[0.05] shadow-sm text-[#1D211F] rounded-2xl rounded-tl-md"}`}>
                  {m.body}
                </div>
                {m.attachments && m.attachments.length > 0 && (
                  <div className={`flex flex-wrap gap-1.5 mt-2 ${isMine ? "justify-end" : ""}`}>
                    {m.attachments.map((a) => (
                      <a key={a.id} href={`${API}/portal/tickets/by-token/${token}/attachments/${a.id}/file`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-neutral-200 rounded-lg hover:border-[#24593C] text-[13px] transition-colors" data-testid={`portal-att-${a.id}`}>
                        {a.kind === "image" ? <ImageIcon size={14} weight="duotone" className="text-[#3E5AA8]" /> : <FilePdf size={14} weight="duotone" className="text-[#C83B3B]" />}
                        <span className="max-w-[160px] truncate">{a.original_filename}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply */}
      {canReply ? (
        <form onSubmit={postMessage} className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/[0.05] p-5 sm:p-6 mt-6" data-testid="portal-reply-form">
          <Label className="flex items-center gap-2 text-sm font-medium text-[#1D211F] mb-2"><ChatCircle size={16} weight="bold" className="text-[#24593C]" /> Votre réponse</Label>
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Rédigez votre message..."
            className="rounded-xl bg-white border-neutral-200 p-4 text-base min-h-[110px] mb-4 focus-visible:ring-2 focus-visible:ring-[#24593C]/25 focus-visible:border-[#24593C] focus-visible:ring-offset-0 resize-none"
            data-testid="portal-reply-input"
          />
          <AttachmentPicker files={files} setFiles={setFiles} compact testIdPrefix="portal-reply" />
          <div className="flex justify-end mt-4">
            <Button type="submit" disabled={posting} className="w-full sm:w-auto bg-[#24593C] hover:bg-[#1B432D] text-white rounded-full h-14 px-6 text-base font-medium gap-2 active:scale-[0.98] transition-all" data-testid="portal-send-reply">
              <PaperPlaneTilt size={18} weight="bold" />
              {posting ? "Envoi..." : "Envoyer"}
            </Button>
          </div>
        </form>
      ) : (
        <div className="mt-6 rounded-2xl border border-neutral-200 bg-[#F7F6F2] px-4 py-5 flex items-center gap-3 text-[15px] text-[#68706A]" data-testid="ticket-closed-notice">
          <LockKey size={20} weight="duotone" className="shrink-0" />
          <span>Ce ticket est <span className="font-medium text-[#1D211F]">clôturé</span>. Pour toute nouvelle demande, merci d'ouvrir un nouveau ticket.</span>
        </div>
      )}
    </section>
  );
}
